// @ts-nocheck

/**
 * MSI3 Revenue Test Utilities
 * 
 * Pure functions for testing revenue calculations without Firebase dependencies.
 * These utilities match the production logic exactly for verification.
 */

import { MSI3_CONSTANTS } from './constants';

const { REVENUE_TIERS, PRICING } = MSI3_CONSTANTS;

// ============================================
// Revenue Calculation Utilities
// ============================================

/**
 * Calculate revenue share percentage from market penetration
 * @param {number} penetrationPercent - Market penetration (0-100)
 * @returns {number} Revenue share percentage (75, 50, or 25)
 */
export const calculateRevenueSharePercent = (penetrationPercent) => {
  if (penetrationPercent <= REVENUE_TIERS.LOW_PENETRATION.maxPercent) {
    return REVENUE_TIERS.LOW_PENETRATION.revenueShare; // 75%
  }
  if (penetrationPercent <= REVENUE_TIERS.MEDIUM_PENETRATION.maxPercent) {
    return REVENUE_TIERS.MEDIUM_PENETRATION.revenueShare; // 50%
  }
  return REVENUE_TIERS.HIGH_PENETRATION.revenueShare; // 25%
};

/**
 * Calculate monthly revenue for a subscription
 * @param {string} subscriptionTier - free, small, medium, large
 * @param {number} revenueSharePct - 75, 50, or 25
 * @param {string} pricingTier - TIER_1, TIER_2, TIER_3, TIER_4
 * @returns {number} Monthly revenue amount in USD
 */
export const calculateMonthlyRevenue = (subscriptionTier, revenueSharePct, pricingTier = 'TIER_1') => {
  const annualPricing = PRICING[pricingTier] || PRICING.TIER_1;
  const annualAmount = annualPricing[subscriptionTier] || 0;
  const monthlySubscription = annualAmount / 12;
  const monthlyRevenue = monthlySubscription * (revenueSharePct / 100);
  return parseFloat(monthlyRevenue.toFixed(4));
};

/**
 * Calculate annual revenue for a subscription
 * @param {string} subscriptionTier - free, small, medium, large
 * @param {number} revenueSharePct - 75, 50, or 25
 * @param {string} pricingTier - TIER_1, TIER_2, TIER_3, TIER_4
 * @returns {number} Annual revenue amount in USD
 */
export const calculateAnnualRevenue = (subscriptionTier, revenueSharePct, pricingTier = 'TIER_1') => {
  return calculateMonthlyRevenue(subscriptionTier, revenueSharePct, pricingTier) * 12;
};

/**
 * Calculate market penetration percentage
 * @param {number} enrolledPatients - Number of enrolled patients
 * @param {number} addressableMarket - Total addressable market size
 * @returns {number} Penetration percentage (0-100)
 */
export const calculatePenetrationPercent = (enrolledPatients, addressableMarket) => {
  if (!addressableMarket || addressableMarket <= 0) return 0;
  return (enrolledPatients / addressableMarket) * 100;
};

/**
 * Get the revenue tier name for a penetration percentage
 * @param {number} penetrationPercent - Market penetration (0-100)
 * @returns {string} Tier name: 'LOW_PENETRATION', 'MEDIUM_PENETRATION', or 'HIGH_PENETRATION'
 */
export const getRevenueTierName = (penetrationPercent) => {
  if (penetrationPercent <= REVENUE_TIERS.LOW_PENETRATION.maxPercent) {
    return 'LOW_PENETRATION';
  }
  if (penetrationPercent <= REVENUE_TIERS.MEDIUM_PENETRATION.maxPercent) {
    return 'MEDIUM_PENETRATION';
  }
  return 'HIGH_PENETRATION';
};

// ============================================
// Revenue Projection Utilities
// ============================================

/**
 * Project monthly revenue for a city based on enrollment data
 * @param {Object} options - Projection options
 * @returns {Object} Revenue projection
 */
export const projectCityRevenue = ({
  totalEnrollments,
  tierBreakdown, // { free: n, small: n, medium: n, large: n }
  addressableMarket,
  pricingTier = 'TIER_1',
}) => {
  const penetration = calculatePenetrationPercent(totalEnrollments, addressableMarket);
  const revenueSharePct = calculateRevenueSharePercent(penetration);
  
  const revenueByTier = {
    free: 0,
    small: calculateMonthlyRevenue('small', revenueSharePct, pricingTier) * (tierBreakdown.small || 0),
    medium: calculateMonthlyRevenue('medium', revenueSharePct, pricingTier) * (tierBreakdown.medium || 0),
    large: calculateMonthlyRevenue('large', revenueSharePct, pricingTier) * (tierBreakdown.large || 0),
  };
  
  const totalMonthlyRevenue = Object.values(revenueByTier).reduce((sum, val) => sum + val, 0);
  
  return {
    penetration: parseFloat(penetration.toFixed(2)),
    revenueTier: getRevenueTierName(penetration),
    revenueSharePct,
    revenueByTier: {
      free: parseFloat(revenueByTier.free.toFixed(2)),
      small: parseFloat(revenueByTier.small.toFixed(2)),
      medium: parseFloat(revenueByTier.medium.toFixed(2)),
      large: parseFloat(revenueByTier.large.toFixed(2)),
    },
    totalMonthlyRevenue: parseFloat(totalMonthlyRevenue.toFixed(2)),
    totalAnnualRevenue: parseFloat((totalMonthlyRevenue * 12).toFixed(2)),
    paidPatients: (tierBreakdown.small || 0) + (tierBreakdown.medium || 0) + (tierBreakdown.large || 0),
    freePatients: tierBreakdown.free || 0,
  };
};

/**
 * Project facility revenue
 * @param {Object} options - Projection options
 * @returns {Object} Revenue projection
 */
export const projectFacilityRevenue = ({
  enrollments, // Array of enrollment objects with { subscriptionTier, lockedRevenueSharePercentage, revenueShareStatus }
  pricingTier = 'TIER_1',
}) => {
  const activeEnrollments = enrollments.filter(e => e.revenueShareStatus === 'active');
  
  let totalMonthly = 0;
  const tierCounts = { free: 0, small: 0, medium: 0, large: 0 };
  
  activeEnrollments.forEach(enrollment => {
    const { subscriptionTier, lockedRevenueSharePercentage } = enrollment;
    tierCounts[subscriptionTier] = (tierCounts[subscriptionTier] || 0) + 1;
    
    if (subscriptionTier !== 'free') {
      totalMonthly += calculateMonthlyRevenue(subscriptionTier, lockedRevenueSharePercentage, pricingTier);
    }
  });
  
  const averageRevenueShare = activeEnrollments.length > 0
    ? activeEnrollments.reduce((sum, e) => sum + e.lockedRevenueSharePercentage, 0) / activeEnrollments.length
    : 0;
  
  return {
    totalEnrollments: enrollments.length,
    activeEnrollments: activeEnrollments.length,
    pausedEnrollments: enrollments.filter(e => e.revenueShareStatus === 'paused').length,
    tierCounts,
    averageRevenueShare: parseFloat(averageRevenueShare.toFixed(1)),
    totalMonthlyRevenue: parseFloat(totalMonthly.toFixed(2)),
    totalAnnualRevenue: parseFloat((totalMonthly * 12).toFixed(2)),
  };
};

// ============================================
// Test Scenario Builders
// ============================================

/**
 * Generate a revenue scenario for testing
 * @param {string} scenarioType - 'low_penetration', 'medium_penetration', 'high_penetration', 'mixed'
 * @returns {Object} Test scenario data
 */
export const generateRevenueScenario = (scenarioType) => {
  const scenarios = {
    low_penetration: {
      name: 'Low Penetration Market (0-33%)',
      addressableMarket: 100000,
      totalEnrollments: 20000, // 20%
      tierBreakdown: { free: 15000, small: 3000, medium: 1500, large: 500 },
    },
    medium_penetration: {
      name: 'Medium Penetration Market (34-66%)',
      addressableMarket: 100000,
      totalEnrollments: 50000, // 50%
      tierBreakdown: { free: 35000, small: 8000, medium: 5000, large: 2000 },
    },
    high_penetration: {
      name: 'High Penetration Market (67-100%)',
      addressableMarket: 100000,
      totalEnrollments: 75000, // 75%
      tierBreakdown: { free: 50000, small: 12000, medium: 8000, large: 5000 },
    },
    early_stage: {
      name: 'Early Stage Market (<5%)',
      addressableMarket: 440000,
      totalEnrollments: 10000, // ~2.3%
      tierBreakdown: { free: 8000, small: 1200, medium: 600, large: 200 },
    },
    near_threshold_33: {
      name: 'Near 33% Threshold',
      addressableMarket: 100000,
      totalEnrollments: 32500, // 32.5% - just under threshold
      tierBreakdown: { free: 25000, small: 4500, medium: 2000, large: 1000 },
    },
    near_threshold_66: {
      name: 'Near 66% Threshold',
      addressableMarket: 100000,
      totalEnrollments: 65500, // 65.5% - just under threshold
      tierBreakdown: { free: 45000, small: 12000, medium: 6000, large: 2500 },
    },
  };
  
  const scenario = scenarios[scenarioType];
  if (!scenario) {
    throw new Error(`Unknown scenario type: ${scenarioType}. Available: ${Object.keys(scenarios).join(', ')}`);
  }
  
  const projection = projectCityRevenue({
    totalEnrollments: scenario.totalEnrollments,
    tierBreakdown: scenario.tierBreakdown,
    addressableMarket: scenario.addressableMarket,
  });
  
  return {
    ...scenario,
    projection,
  };
};

/**
 * Compare revenue at different penetration levels
 * Shows impact of penetration changes on facility revenue
 */
export const compareRevenueAtPenetrationLevels = (subscriptionTier, pricingTier = 'TIER_1') => {
  const levels = [10, 20, 30, 33, 34, 50, 60, 66, 67, 80, 90, 100];
  
  return levels.map(penetration => ({
    penetration,
    revenueSharePct: calculateRevenueSharePercent(penetration),
    monthlyRevenue: calculateMonthlyRevenue(subscriptionTier, calculateRevenueSharePercent(penetration), pricingTier),
    annualRevenue: calculateAnnualRevenue(subscriptionTier, calculateRevenueSharePercent(penetration), pricingTier),
    tier: getRevenueTierName(penetration),
  }));
};

/**
 * Calculate revenue impact of tier upgrade
 */
export const calculateUpgradeImpact = (fromTier, toTier, revenueSharePct, pricingTier = 'TIER_1') => {
  const fromMonthly = calculateMonthlyRevenue(fromTier, revenueSharePct, pricingTier);
  const toMonthly = calculateMonthlyRevenue(toTier, revenueSharePct, pricingTier);
  
  const monthlyIncrease = toMonthly - fromMonthly;
  const annualIncrease = monthlyIncrease * 12;
  const percentIncrease = fromMonthly > 0 ? ((toMonthly - fromMonthly) / fromMonthly) * 100 : 0;
  
  return {
    fromTier,
    toTier,
    revenueSharePct,
    fromMonthlyRevenue: parseFloat(fromMonthly.toFixed(4)),
    toMonthlyRevenue: parseFloat(toMonthly.toFixed(4)),
    monthlyIncrease: parseFloat(monthlyIncrease.toFixed(4)),
    annualIncrease: parseFloat(annualIncrease.toFixed(4)),
    percentIncrease: parseFloat(percentIncrease.toFixed(2)),
  };
};

// ============================================
// Validation Utilities
// ============================================

/**
 * Validate that production revenue calculation matches expected value
 * Used for regression testing
 */
export const validateRevenueCalculation = (testCases) => {
  const results = testCases.map(({ tier, revenueSharePct, pricingTier, expected }) => {
    const actual = calculateMonthlyRevenue(tier, revenueSharePct, pricingTier);
    const passed = Math.abs(actual - expected) < 0.0001; // Allow small floating point variance
    
    return {
      tier,
      revenueSharePct,
      pricingTier,
      expected,
      actual,
      passed,
      error: passed ? null : `Expected ${expected}, got ${actual}`,
    };
  });
  
  const allPassed = results.every(r => r.passed);
  
  return {
    allPassed,
    passCount: results.filter(r => r.passed).length,
    failCount: results.filter(r => !r.passed).length,
    results,
  };
};

// Pre-defined test cases for Kenya Tier 1 pricing
export const TIER_1_TEST_CASES = [
  // 75% revenue share (0-33% penetration)
  { tier: 'free', revenueSharePct: 75, pricingTier: 'TIER_1', expected: 0 },
  { tier: 'small', revenueSharePct: 75, pricingTier: 'TIER_1', expected: 0.375 },
  { tier: 'medium', revenueSharePct: 75, pricingTier: 'TIER_1', expected: 0.6069 },
  { tier: 'large', revenueSharePct: 75, pricingTier: 'TIER_1', expected: 0.7425 },
  
  // 50% revenue share (34-66% penetration)
  { tier: 'free', revenueSharePct: 50, pricingTier: 'TIER_1', expected: 0 },
  { tier: 'small', revenueSharePct: 50, pricingTier: 'TIER_1', expected: 0.25 },
  { tier: 'medium', revenueSharePct: 50, pricingTier: 'TIER_1', expected: 0.4046 },
  { tier: 'large', revenueSharePct: 50, pricingTier: 'TIER_1', expected: 0.495 },
  
  // 25% revenue share (67-100% penetration)
  { tier: 'free', revenueSharePct: 25, pricingTier: 'TIER_1', expected: 0 },
  { tier: 'small', revenueSharePct: 25, pricingTier: 'TIER_1', expected: 0.125 },
  { tier: 'medium', revenueSharePct: 25, pricingTier: 'TIER_1', expected: 0.2023 },
  { tier: 'large', revenueSharePct: 25, pricingTier: 'TIER_1', expected: 0.2475 },
];

// ============================================
// Export for window API
// ============================================

if (typeof window !== 'undefined') {
  window.__MSI3_REVENUE_TEST__ = {
    calculateRevenueSharePercent,
    calculateMonthlyRevenue,
    calculateAnnualRevenue,
    calculatePenetrationPercent,
    getRevenueTierName,
    projectCityRevenue,
    projectFacilityRevenue,
    generateRevenueScenario,
    compareRevenueAtPenetrationLevels,
    calculateUpgradeImpact,
    validateRevenueCalculation,
    TIER_1_TEST_CASES,
  };
  
}

const revenueTestUtilsExports = {
  calculateRevenueSharePercent,
  calculateMonthlyRevenue,
  calculateAnnualRevenue,
  calculatePenetrationPercent,
  getRevenueTierName,
  projectCityRevenue,
  projectFacilityRevenue,
  generateRevenueScenario,
  compareRevenueAtPenetrationLevels,
  calculateUpgradeImpact,
  validateRevenueCalculation,
  TIER_1_TEST_CASES,
};

export default revenueTestUtilsExports;

